import React, { useState, useCallback } from "react";
import { Pressable, ScrollView, View } from "react-native";
import Text from "@components/CustomText";
import HeaderWithBackAndTitle from "@components/HeaderWithBackAndTitle";
import toast from "@utils/toast";
import {
  submitKycFromContext,
  getBlockedErrorMessage,
} from "@services/onboardingApi";
import { commonStyles, documentOwnershipStyles as styles } from "./styles";
import { useOnboarding } from "./OnboardingContext";
import { useFlowNavigation } from "./useFlowNavigation";
import type { StackNavigationProp } from "@react-navigation/stack";
import type { OnboardingStackParamList } from "./types";

interface DocumentOwnershipProps {
  navigation: StackNavigationProp<
    OnboardingStackParamList,
    "DocumentOwnership"
  >;
}

type OwnershipOption = "self" | "family";

interface OwnerOption {
  key: OwnershipOption;
  label: string;
}

const OWNERSHIP_OPTIONS: OwnerOption[] = [
  { key: "self", label: "My own documents" },
  { key: "family", label: "A family member's" },
];

const DocumentOwnership: React.FC<DocumentOwnershipProps> = ({
  navigation,
}) => {
  const { data, updateData } = useOnboarding();
  const { goToNextStep, isLagganFlow, getDocumentsConfig } =
    useFlowNavigation();
  const [selected, setSelected] = useState<OwnershipOption | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const onContinue = useCallback(async () => {
    const ownership = selected ?? "self";
    updateData({ documentOwnership: ownership });

    if (ownership === "family") {
      // Family KYC — YourDocuments collects the personal doc; skip_if handles routing
      goToNextStep({ is_family_kyc: true });
      return;
    }

    // Self ownership — laggan defers KYC submit to AddressDetails
    if (isLagganFlow()) {
      goToNextStep({ is_family_kyc: false });
      return;
    }

    // Normal self — this is the final KYC step; submit and advance to SignUpSuccessful
    setIsSubmitting(true);
    try {
      await submitKycFromContext(
        { ...data, documentOwnership: ownership },
        getDocumentsConfig(),
      );
      goToNextStep({ is_family_kyc: false });
    } catch (err: unknown) {
      const msg = (err as Error)?.message || "";
      if (msg.startsWith("MISSING:")) {
        toast.showLongToast(`Missing ${msg.replace("MISSING:", "")}`);
      } else {
        const axiosErr = err as { response?: { data?: { message?: string } } };
        toast.showLongToast(
          getBlockedErrorMessage(err) ||
            axiosErr?.response?.data?.message ||
            "Failed to submit KYC. Please try again.",
        );
      }
    } finally {
      setIsSubmitting(false);
    }
  }, [
    selected,
    data,
    updateData,
    goToNextStep,
    isLagganFlow,
    getDocumentsConfig,
  ]);

  return (
    <>
      <HeaderWithBackAndTitle
        headertitle="Document Ownership"
        navigation={navigation}
        hideShadow
      />

      <ScrollView
        contentContainerStyle={styles.scrollContent}
        showsVerticalScrollIndicator={false}
      >
        <Text variant="title_18" style={styles.title}>
          Whose KYC documents did you upload?
        </Text>

        {OWNERSHIP_OPTIONS.map(({ key, label }) => {
          const isSelected = selected === key;
          return (
            <Pressable
              key={key}
              style={[
                styles.radioOption,
                isSelected && styles.radioOptionSelected,
              ]}
              onPress={() => setSelected(key)}
              accessibilityRole="radio"
              accessibilityState={{ selected: isSelected }}
              accessibilityLabel={label}
            >
              <View
                style={[
                  styles.radioCircle,
                  isSelected && styles.radioCircleSelected,
                ]}
              >
                {isSelected && <View style={styles.radioCircleInner} />}
              </View>
              <Text
                variant="body_text_2"
                style={[
                  styles.radioLabel,
                  isSelected && styles.radioLabelSelected,
                ]}
              >
                {label}
              </Text>
            </Pressable>
          );
        })}
      </ScrollView>

      <View style={styles.bottomBar}>
        <Pressable
          style={[commonStyles.primaryButton]}
          disabled={isSubmitting}
          onPress={onContinue}
          accessibilityRole="button"
          accessibilityLabel="Continue"
        >
          <Text variant="title_16" style={commonStyles.primaryButtonText}>
            Continue
          </Text>
        </Pressable>
      </View>
    </>
  );
};

export default DocumentOwnership;
